import * as React from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TenantContext, UserContext } from './contexts/ContextWrapper';
import MainPage from './MainPage';
import { useSettingsFetch } from './hooks/useSettingsFetch';
import { ScrollablePane, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { CenterComponent } from './components/common/CenterComponent';
import NavigationWrapper from './components/navigation/NavigationWrapper';
import { AuthContext } from './contexts/AuthContext';
import Home from './Home';
import { useSession } from './hooks/useSession';
import { UserAdminItem } from './interfaces/UserAdminItem';
import { apiUris } from './helpers/SettingsMisc';
import { IsVerbose } from './helpers/Console';
import { NON_DOMAIN_PAGES } from './helpers/MenuHelper';

// Lazy load components for better initial bundle size
const Academy = React.lazy(() => import('./components/academy/Academy'));
const Domains = React.lazy(() => import('./components/domains/Domains'));
const Customers = React.lazy(() => import('./components/customers/Customers'));
const AdminAccounts = React.lazy(() => import('./components/adminaccounts/AdminAccounts'));
const News = React.lazy(() => import('./components/news/News'));
const EsefTaxonomy = React.lazy(() => import('./components/esef/EsefTaxonomy'));
const AIAdmin = React.lazy(() => import('./components/ai/AIAdmin'));

// Fallback spinner for lazy loaded components
const LazyFallback = () => (
	<CenterComponent>
		<Spinner size={SpinnerSize.large} label='Loading...' />
	</CenterComponent>
);

const Body: React.FC = () => {
	const { api, domain } = useParams<'api' | 'domain'>();

	const [selectedPage, setSelectedPage] = React.useState<string>('home');

	const { userContext } = React.useContext(UserContext);
	const { accessToken } = React.useContext(AuthContext);
	const { tenantContext, setTenantContext } = React.useContext(TenantContext);

	const navigate = useNavigate();
	const location = useLocation();
	const { UpdateSettings, isLoadingSettings } = useSettingsFetch()
	const { LoginSession } = useSession()

	// Ref to track if the initial fetch has been attempted for the current api/domain
	const initialFetchAttempted = React.useRef(false);
	// Ref to store fetchAndUpdateSettings to avoid including it in dependencies
	const fetchAndUpdateSettingsRef = React.useRef<() => Promise<void>>();

	const changePage = React.useCallback((newPage: string) => {
		setSelectedPage(prevPage => {
			if (prevPage !== newPage && api && domain) {
				navigate(`/${api}/${domain}/${newPage}`);
			}
			return newPage;
		});
	}, [api, domain, navigate]);

	const fetchAndUpdateSettings = React.useCallback(async () => {
		let data: UserAdminItem;

		if (!userContext.userData.Domains) {
			try {
				// Wrap the callback-based LoginSession in a promise
				data = await new Promise((resolve, reject) => {
					LoginSession(
						(data) => resolve(data),
						(error) => reject(error),
						accessToken!
					);
				});
			} catch (error) {
				console.error('Failed to fetch user data:', error);
				console.warn('⚠️ Redirecting to root page due to user data fetch failure');
				navigate('/');
				return;
			}
		} else {
			data = userContext.userData;
		}

		// Now perform the validDomain check
		const validApi = data.API?.some((a) => a.APIName.toLowerCase() === api?.toLowerCase());

		const validDomain = data.Domains?.some((d) =>
			d.APIName.toLowerCase() === api?.toLowerCase() &&
			d.DomainName.toLowerCase() === domain?.toLowerCase()
		);

		if (domain && NON_DOMAIN_PAGES.includes(domain.toLowerCase())) {
			setTenantContext((prev) => ({
				...prev,
				api: api?.toLowerCase() ?? '',
				apiUri: apiUris(api!),
				tenantName: domain,
			}));
			return;
		}

		if (api && domain && validDomain) {
			try {
				await UpdateSettings(api, domain, data);
				// navigate(`/${api}/${domain}/${selectedPage}`);
			} catch (error) {
				console.error('Failed to update settings:', error);
			}
			return
		}

		if (validApi) {
			try {
				const domainData = data.Domains?.find((d) => d.APIName.toLowerCase() === api?.toLowerCase());
				if (domainData) {
					await UpdateSettings(api!, domainData.DomainName, data);
					navigate(`/${api}/${domainData.DomainName}/${selectedPage || 'home'}`);
				} else {
					// Handle case where no domain is found for the valid API, though this might indicate a data issue
					console.error('No domain found for API:', api);
					navigate('/login', { state: { from: location.pathname } });
				}
			} catch (error) {
				console.error('Failed to update settings:', error);
			}
			return
		}

		if (!validApi) {
			console.error('Invalid API:', api, data);
			navigate('/login', { state: { from: location.pathname } });
		}

	}, [userContext, domain, api, LoginSession, accessToken, UpdateSettings, selectedPage, navigate, setTenantContext]);

	// Update the ref whenever fetchAndUpdateSettings changes
	React.useEffect(() => {
		fetchAndUpdateSettingsRef.current = fetchAndUpdateSettings;
	}, [fetchAndUpdateSettings]);

	React.useEffect(() => {
		// Reset the flag if api or domain changes
		initialFetchAttempted.current = false;
	}, [api, domain]);

	React.useEffect(() => {
		const abortController = new AbortController();

		const attemptFetch = async () => {
			if (abortController.signal.aborted) return;

			if (accessToken && !initialFetchAttempted.current) {
				initialFetchAttempted.current = true; // Mark as attempted
				if (fetchAndUpdateSettingsRef.current) {
					await fetchAndUpdateSettingsRef.current();
				}
			} else if (!accessToken && !abortController.signal.aborted) {
				IsVerbose() && console.log('No access token, redirecting to login from useEffect.');
				navigate('/login', { state: { from: location.pathname } });
			}
		};

		attemptFetch();

		return () => {
			abortController.abort();
		};
		// Dependencies only include values that should trigger a re-fetch
		// fetchAndUpdateSettings is accessed via ref to prevent circular updates
	}, [api, domain, accessToken, navigate, location.pathname]);

	if (isLoadingSettings || !userContext.userData || !tenantContext.api || !tenantContext.tenantName || !tenantContext.settings) {
		return (
			<CenterComponent>
				<Spinner className='spinner' size={SpinnerSize.large} label='Loading data...' />
			</CenterComponent>
		);
	}

	const adminPage = domain?.toLowerCase();
	if (adminPage && NON_DOMAIN_PAGES.includes(adminPage)) {
		let content: JSX.Element = <></>;
		switch (adminPage) {
			case 'academy':
				content = <Academy />;
				break;
			case 'customers':
				content = <Customers />;
				break;
			case 'domains':
				content = (
					<Domains
						updateUserData={() => { LoginSession(() => { }, () => { }, accessToken!) }}
						userData={userContext.userData}
					/>
				);
				break;
			case 'adminaccounts':
				content = (
					<AdminAccounts
						accessToken={accessToken!}
						userData={userContext.userData}
					/>
				);
				break;
			case 'news':
				content = <News />;
				break;
			case 'esefadmin':
				content = <EsefTaxonomy />;
				break;
			case 'aiadmin':
				content = <AIAdmin />;
				break;
		}

		const suspended = (
			<React.Suspense fallback={<LazyFallback />}>
				{content}
			</React.Suspense>
		);

		return (
			<Stack.Item className="ms-Grid-col ms-bgColor-gray10" styles={{ root: { height: '100%' } }}>
				{/* Academy manages its own scrolling. */}
				{adminPage === 'academy' ? suspended : <ScrollablePane>{suspended}</ScrollablePane>}
			</Stack.Item>
		)
	}

	return (
		<NavigationWrapper
			setSelectedPage={changePage}
			selectedPage={selectedPage}
		>
			<Routes>
				<Route index element={<Home />} />
				<Route
					path=":page/*"
					element={
						tenantContext.settings ? (
							<MainPage setSelectedPage={setSelectedPage} />
						) : (
							<CenterComponent>
								<Spinner size={SpinnerSize.large} label="Loading settings..." />
							</CenterComponent>
						)
					}
				/>
			</Routes>
		</NavigationWrapper>
	);
}

export default Body;
